import { flow, Instance, types, getEnv } from "mobx-state-tree";
import { endpoints } from "../api/ApiEndpoints";
import { Column, ColumnType, DisplayValues } from "../../components/datatable/interfaces";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";

export interface PriceTier {
    id: number,
    minimum: number,
    maximum: number,
    discount: number,
    ratio: number
}

export const emptyPriceTier: PriceTier = {
    id: 0,
    minimum: 0,
    maximum: 0,
    discount: 0,
    ratio: 0
}

const columns : Column[] = [
    {
        key: "minimum",
        name: "shop.minimum",
        type: ColumnType.Number
    },
    {
        key: "maximum",
        name: "shop.maximum",
        type: ColumnType.Number
    },
    {
        key: "ratio",
        name: "shop.ratio",
        type: ColumnType.Number,
        containsLink: true,
    },
    {
        key: "discount",
        name: "shop.discount",
        type: ColumnType.String,
        containsLink: true,
    }
]

const PricesModel = types.model({
    prices: types.optional(types.frozen<PriceTier[]>(), [emptyPriceTier, emptyPriceTier]),
    calculatedPrices: types.optional(types.frozen<number[]>(), [0])
})

export const PricesStorageModel = types
    .compose(PricesModel, DataGridStorageModel)
    .named("PricesStorageModel")
    .actions((self) => {
        return {
            mapResponse(response: PriceTier[]) {
                self.prices = response;
                self.gridColumns = columns;
                self.data = {
                    totalCount: response.length,
                    items: response.map(self.mapItem)
                }
                var temp: number[] = [];
                response.forEach((priceTier: PriceTier) => {
                    for (let i = priceTier.minimum; i < priceTier.maximum + 1; i++) {
                        temp.push(Math.ceil(i * priceTier.ratio))
                    }
                })
                self.calculatedPrices = [...temp];
            },
            mapItem(item: PriceTier) {
                var displayValues: DisplayValues = {
                    minimum: {value: item.minimum, toDisplay: item.minimum},
                    maximum: {value: item.maximum, toDisplay: item.maximum},
                    ratio: {value: item.ratio, toDisplay: item.ratio},
                    discount: {value: item.discount, toDisplay: item.discount + "%"}
                }
                return {
                    displayValues: displayValues,
                    id: item.id
                }
            },
            getCheckoutSessionUrl: flow(function* (coins: number) {
                const endpoint = endpoints.get("create_shop_checkout_session");
                endpoint.queryParams = `coins=${coins}`;
                const sessionUrl = yield getEnv(self).api.get(endpoint);
                window.location.href = sessionUrl;
            }),
            calculateCoins: function (currency: number) {
                var index = self.calculatedPrices.lastIndexOf(currency);
                if (index < 0) return Math.ceil(currency / self.prices[self.prices.length - 1].ratio);
                return index;
            },
            calculateCurrency: function (coins: number) {
                if (coins > self.calculatedPrices.length - 1) return Math.ceil(coins * self.prices[self.prices.length - 1].ratio);
                return self.calculatedPrices[coins];
            },
            getDiscount: function (coins: number) {
                var discount = self.prices.find(p => coins >= p.minimum && coins <= p.maximum)?.discount;
                return discount === undefined ? self.prices[self.prices.length - 1].discount : discount;
            }
        }
    })
    .views((self) => ({
        get endpoint() {
            return endpoints.get("get_prices");
        },
        get isLoading() {
            return self.prices.length < 3;
        },
        get coinAxis() {
            var steps = getEnv(self).activityPricesStorage.conversionPrice;
            return Array(Math.floor(this.maximumCoins / steps)).fill(0).map((_, index) => {
                return index * steps;
            });
        },
        get currencyAxis() {
            return this.coinAxis.map((value: number) => {
                return self.calculateCurrency(value);
            });
        },
        get dataset() {
            return this.currencyAxis.map((value: number) => {
                return { x: this.currencyAxis, y: this.coinAxis };
            });
        },
        get minimumCoins() {
            return self.prices[0].minimum;
        },
        get maximumCoins() {
            return self.prices[self.prices.length - 1].maximum;
        },
        get minimumCurrency() {
            return self.calculateCurrency(this.minimumCoins);
        },
        get maximumCurrency() {
            return self.calculateCurrency(this.maximumCoins);
        },
        get totalTiers() {
            return self.prices.length;
        },
        get minimumRatio() {
            return self.prices[0].ratio;
        }
    }));

export type PricesStorage = Instance<typeof PricesStorageModel>;
